import { API, Auth, Storage, graphqlOperation } from 'aws-amplify';

import { MAINTENANCE_EVENTS } from '../../../../components/ExpenseHistoryTable/maintenanceEvents';
import * as customMutations from '../../../../graphql/customMutations';
import * as queries from '../../../../graphql/queries';

const STORAGE_LEVEL = 'public';
// The app stores documentName RELATIVE to the `expense/` folder and reads it back as
// `Storage.get('expense/' + documentName)` (see DriverSolutions/documentRow.js). Keep the
// same convention here: documentName is the path under `expense/`, and we only prepend
// the folder for the actual S3 put/remove. Storing the full key (with `expense/`) double-
// prefixes on read and shows the wrong name (CP-815).
const STORAGE_FOLDER = 'expense/';
const MAINTENANCE_SUBPATH = 'maintenance';

const generateUuid = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

// Receipts live on the Expense's ExpenseItem line items (ExpenseItem.documentName),
// not on the top-level Expense — that's where the rest of the app reads/writes them
// (see DriverSolutions/documentRow.js). Maintenance must target the same place
// (CP-815) or copied/uploaded docs are invisible everywhere except this screen.
export const firstItemWithDoc = expense => expense?.expenseItem?.items?.find(item => item.documentName) ?? null;

const primaryItem = expense => expense?.expenseItem?.items?.[0] ?? null;

// Amplify GraphQL rejections arrive as { errors: [{ message }] }; surface the real
// reason instead of a generic "Failed to save" (CP-815 #1).
export const getErrorMessage = (error, fallback = 'Failed to save. Please try again.') =>
  error?.errors?.[0]?.message || error?.message || fallback;

const createMaintenanceAudit = async (expenseId, userId, event) => {
  try {
    await API.graphql(
      graphqlOperation(customMutations.createAudit, {
        input: {
          // CP-812: the descriptive text now lives in `event`; objectData is left blank.
          // The schema types objectData as String! (non-null), so it is an empty string
          // rather than null.
          event,
          eventTime: new Date().toISOString(),
          objectName: 'Expense',
          objectId: expenseId,
          objectData: '',
          userAuditId: userId,
        },
      }),
    );
  } catch {
    // audit failure should not block the save
  }
};

export const searchExpenseById = async expenseId => {
  const response = await API.graphql({
    query: queries.getExpense,
    variables: { id: expenseId },
  });
  return response.data.getExpense ?? null;
};

export const saveManualExpense = async ({
  expenseId,
  userId,
  approvedFlag,
  statusTransmitted,
  payrollWeekEndDate,
  notes,
}) => {
  // updatedBy ties the DynamoDB-stream audit Lambda (cp-dynamodb-audit) record to the
  // maintenance user. Without it the Expense keeps its prior updatedBy and the auto
  // "Status Changed" audit is attributed to the previous editor (CP-787 #3 "John" bug).
  const input = { id: expenseId, notes, updatedBy: userId };
  if (approvedFlag) input.ok = 'T';
  if (statusTransmitted) input.status = 'Transmitted';
  if (payrollWeekEndDate) input.payWeekEnding = payrollWeekEndDate;
  await API.graphql(graphqlOperation(customMutations.updateExpense, { input }));
  await createMaintenanceAudit(expenseId, userId, MAINTENANCE_EVENTS.MANUAL_EXPENSE);
};

export const saveDeletedExpense = async ({
  expenseId,
  userId,
  clearApprovedFlag,
  statusReadyToBill,
  clearPayrollDate,
  notes,
}) => {
  // updatedBy: attribute the auto status-change audit to the maintenance user (see saveManualExpense).
  const input = { id: expenseId, notes, updatedBy: userId };
  if (clearApprovedFlag) input.ok = null;
  if (statusReadyToBill) input.status = 'Ready to Bill';
  if (clearPayrollDate) input.payWeekEnding = null;
  await API.graphql(graphqlOperation(customMutations.updateExpense, { input }));
  await createMaintenanceAudit(expenseId, userId, MAINTENANCE_EVENTS.DELETE_EXPENSE);
};

export const copyReceiptToExpense = async ({ sourceExpenseId, destinationExpenseId, userId }) => {
  const sourceResponse = await API.graphql({
    query: queries.getExpense,
    variables: { id: sourceExpenseId },
  });
  const sourceItem = firstItemWithDoc(sourceResponse.data.getExpense);
  if (!sourceItem) throw new Error('Source expense has no document');

  const destResponse = await API.graphql({
    query: queries.getExpense,
    variables: { id: destinationExpenseId },
  });
  const destination = destResponse.data.getExpense;
  // CP-815: the destination must be MISSING a receipt — this tool only fills empty
  // expenses. Copying onto one that already has a doc would duplicate it.
  if (firstItemWithDoc(destination)) throw new Error('Destination expense already has a receipt');
  // write to the destination's primary line item — the doc has to land on an
  // ExpenseItem to be visible on the expense.
  const destItem = primaryItem(destination);
  if (!destItem) throw new Error('Destination expense has no line item to attach a document to');

  let uploadedBy = '';
  try {
    const user = await Auth.currentAuthenticatedUser();
    uploadedBy = user.attributes?.name || user.username || '';
  } catch {
    // proceed with empty uploadedBy
  }

  await API.graphql(
    graphqlOperation(customMutations.updateExpenseItem, {
      input: {
        id: destItem.id,
        documentName: sourceItem.documentName,
        documentSize: sourceItem.documentSize ?? null,
        uploadedByName: uploadedBy,
        uploadedDate: new Date().toISOString(),
      },
    }),
  );
  await createMaintenanceAudit(destinationExpenseId, userId, MAINTENANCE_EVENTS.MISSING_RECEIPT);
};

export const uploadDocumentToExpense = async ({ expenseId, file, userId, userName }) => {
  let uploadedByName = userName || '';
  if (!uploadedByName) {
    try {
      const user = await Auth.currentAuthenticatedUser();
      uploadedByName = user.attributes?.name || user.username || '';
    } catch {
      // proceed with empty name
    }
  }

  const uuid = generateUuid();
  // documentName is stored relative to the `expense/` folder; the S3 key prepends it.
  const documentName = `${MAINTENANCE_SUBPATH}/${expenseId}/${uuid}-${file.name}`;
  const storageKey = `${STORAGE_FOLDER}${documentName}`;
  const uploadedDate = new Date().toISOString().substring(0, 10);

  await Storage.put(storageKey, file, {
    level: STORAGE_LEVEL,
    contentType: file.type || 'application/octet-stream',
  });

  try {
    // CP-815: the uploaded doc has to land on an ExpenseItem (where the app reads
    // receipts), not the top-level Expense. Fetch the expense to find its line item.
    const expenseResponse = await API.graphql({
      query: queries.getExpense,
      variables: { id: expenseId },
    });
    const expense = expenseResponse.data.getExpense;
    // CP-815: only add a document to an expense that is MISSING one — prevent duplicates.
    if (firstItemWithDoc(expense)) throw new Error('Expense already has a receipt');
    const destItem = primaryItem(expense);
    if (!destItem) throw new Error('Expense has no line item to attach a document to');

    await API.graphql(
      graphqlOperation(customMutations.updateExpenseItem, {
        input: {
          id: destItem.id,
          documentName,
          documentSize: file.size,
          uploadedByName,
          uploadedDate: new Date().toISOString(),
        },
      }),
    );
    if (userId) {
      await createMaintenanceAudit(expenseId, userId, MAINTENANCE_EVENTS.MISSING_RECEIPT);
    }
  } catch (e) {
    await Storage.remove(storageKey, { level: STORAGE_LEVEL });
    throw e;
  }

  return { fileName: file.name, uploadedBy: uploadedByName, uploadedDate };
};
